/**
 * Service CRUD, bulk operations, and ownership (claim / assign-team, issue #536)
 */

import type { Service } from '../../graphql/types';
import { apiRequest } from './http';
import { createEntityCrudClient, createBulkOperationsClient } from './crudFactory';
import type { BulkOperationResult } from './crudFactory';

export interface CreateServiceInput {
  name: string;
  shortName?: string;
  description?: string;
  serviceType: string;
  teamId: string;
  repo?: string;
  lifecycleStatus?: string;
  tier: string;
}

export interface UpdateServiceInput {
  name?: string;
  shortName?: string;
  description?: string;
  serviceType?: string;
  teamId?: string | null;
  repo?: string;
  lifecycleStatus?: string;
  tier?: string;
}

const client = createEntityCrudClient<Service, CreateServiceInput, UpdateServiceInput>('/services');

/**
 * Create a new service
 */
export async function createService(input: CreateServiceInput): Promise<Service> {
  return client.create(input);
}

/**
 * Update an existing service
 */
export async function updateService(
  serviceId: string,
  input: UpdateServiceInput
): Promise<Service> {
  return client.update(serviceId, input);
}

/**
 * Delete a service
 */
export async function deleteService(serviceId: string, hardDelete: boolean = false): Promise<void> {
  return client.remove(serviceId, hardDelete);
}

const bulkClient =
  createBulkOperationsClient<Pick<UpdateServiceInput, 'lifecycleStatus' | 'tier'>>('/services');

/**
 * Bulk delete services
 */
export async function bulkDeleteServices(
  ids: string[],
  hardDelete = false
): Promise<BulkOperationResult> {
  return bulkClient.bulkDelete(ids, hardDelete);
}

/**
 * Bulk update common fields on multiple services
 */
export async function bulkUpdateServices(
  ids: string[],
  updates: Pick<UpdateServiceInput, 'lifecycleStatus' | 'tier'>
): Promise<BulkOperationResult> {
  return bulkClient.bulkUpdate(ids, updates);
}

/**
 * POST /api/v1/services/:serviceId/claim
 * Claim an unowned service for one of the caller's teams.
 * Returns 409 if the service is already owned.
 */
export async function claimService(serviceId: string, teamId: string): Promise<Service> {
  return apiRequest<Service>(`/services/${serviceId}/claim`, {
    method: 'POST',
    body: JSON.stringify({ teamId }),
  });
}

/**
 * POST /api/v1/services/:serviceId/assign-team
 * Assign (reassign) a service to a team. Non-admins may only assign to teams
 * they belong to (403 otherwise); Administrators may assign to any team.
 */
export async function assignServiceTeam(serviceId: string, teamId: string): Promise<Service> {
  return apiRequest<Service>(`/services/${serviceId}/assign-team`, {
    method: 'POST',
    body: JSON.stringify({ teamId }),
  });
}
